import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, Lock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function VoiceInput({ onTranscript, testid = "voice-input-btn" }) {
  const { user } = useAuth();
  const locked = !user || user.tier === "free";
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setLoading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "recording.webm");
          const { data } = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data?.text) {
            onTranscript?.(data.text);
            toast.success("Voice captured");
          } else {
            toast.message("No speech detected");
          }
        } catch (e) {
          toast.error(formatError(e));
        } finally {
          setLoading(false);
        }
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  if (locked) {
    return (
      <Link
        to="/pricing"
        data-testid={`${testid}-locked`}
        title="Voice input is a Pro feature"
        className="flex items-center justify-center h-11 w-11 rounded-xl border bg-sand-100 border-sand-300 text-stoneink-500 hover:border-terracotta-500 hover:text-terracotta-600 transition-all"
      >
        <Lock size={16} />
      </Link>
    );
  }

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={recording ? stop : start}
      disabled={loading}
      title={recording ? "Stop recording" : "Voice input"}
      className={`flex items-center justify-center h-11 w-11 rounded-xl border transition-all active:scale-95 ${
        recording
          ? "bg-terracotta-500 text-white border-terracotta-500 recording"
          : "bg-white border-sand-300 text-stoneink-700 hover:border-ochre-500 hover:bg-sand-100"
      } ${loading ? "opacity-60 cursor-wait" : ""}`}
    >
      {loading ? (
        <span className="h-4 w-4 rounded-full border-2 border-stoneink-700 border-t-transparent animate-spin" />
      ) : recording ? (
        <Stop weight="fill" size={20} />
      ) : (
        <Microphone weight="duotone" size={20} />
      )}
    </button>
  );
}
